"use client";

import { useState, useRef } from "react";
import { getTranslations } from "@/lib/i18n";
import {
  formatDurationMinutes,
  languageCodeLabel,
} from "@/lib/viator-product-info";

/**
 * De Viator-blokken van EEN activiteit op de dealpagina (certificering F4 +
 * F4b), IN ONZE EIGEN OPMAAK (Dennis 25-09-2026, besluit "Dealpagina: in
 * onze eigen opmaak" -- letterlijk: "aanbiedersfoto's in onze galerij,
 * in- en exclusies in onze 'Deze reis is inclusief'-stijl, de rest onder
 * één uitklapkop 'Praktisch over deze activiteit', Nederlands waar Viator
 * het levert").
 *
 * Drie blokken, in die volgorde:
 *  1. `SupplierPhotoCarousel` -- eigen carrousel-stijl (zelfde
 *     interactiepatroon als `HotelImpressionsCarousel.js`: pijlen, dots,
 *     swipe), geen "van de aanbieder"-label op de foto's zelf.
 *  2. In-/exclusies in de stijl van het "Deze reis is inclusief"-blok
 *     (groen vinkje / grijs kruisje), geen aparte aanbieder-styling.
 *  3. ÉÉN uitklapkop "Praktisch over deze activiteit" (native
 *     `<details>`) met de rest: omschrijving, programma, duur,
 *     aanvullende info, verzamelpunt, tickettype, talen.
 *
 * ⛔ GEEN ANNULERINGSBLOK (Dennis, keuzevraag 25-09-2026 ~15:25, letterlijk:
 * "Wij hebben onze eigen annuleringsvoorwaarden. Op het moment dat de
 * annuleringsvoorwaarden van Viator interessanter zijn, hanteren wij nog
 * steeds onze eigen voorwaarden." en "Men annuleert niet alleen een
 * activiteit; het is een volledige package, en wij hebben onze algemene
 * eigen voorwaarden."). Er komt dus GEEN eigen of afgeleide
 * annuleringstekst per activiteit op de dealpagina, ook niet in onze eigen
 * woorden: de klant valt bij annuleren onder Favotrips pakketvoorwaarden
 * (artikel 7), niet onder een activiteit-specifieke regel. Zie de eerdere
 * `classifyViatorCancellationPolicy` (nu verwijderd uit
 * @/lib/viator-product-info) en de i18n-sleutels `deal.viator.cancellation_*`
 * (nu verwijderd) voor de vorige, nu ingetrokken opzet.
 *
 * ALLEEN PRESENTATIE: krijgt de al gelezen blokken (zie
 * `readViatorActivityContent` in @/lib/viator-product-info) en rendert die.
 * Het ophalen gebeurt in de browser, in ViatorProductContent.jsx, zodat er
 * GEEN Viator-tekst in de server-HTML staat (niet-indexeerbaar). Dit
 * component mag daarom niet rechtstreeks vanuit een servercomponent met
 * Viator-gegevens worden aangeroepen.
 *
 * Leeg of ontbrekend = blok weg: geen lege kopjes, geen "onbekend", geen
 * plaatshouder.
 *
 * TAAL: `content.language` is de taal die de backend echt kon leveren;
 * `content.requestedLanguage` is de sitetaal. Wijken ze af (de backend
 * viel terug op Engels omdat de aanbieder deze taal niet levert), dan
 * tonen we die Engelse tekst gewoon, ZONDER label (Dennis 25-09-2026:
 * "geen labels in het gezicht van de reiziger"; de gezamenlijke
 * instructie: "Waar geen vertaling is: de Engelse tekst, zonder label").
 *
 * Het #3568-viertal (verzamelpunt, tickettype, taalgidsen,
 * annuleringstekst) komt uit ÉÉN Engelse Viator-aanroep. Verzamelpunt en
 * tickettype tonen Viators eigen (Engelse) tekst gewoon rechtstreeks --
 * dat is feitelijke logistieke informatie, geen mening, en heeft geen
 * eigen woorden nodig. ALLEEN de annuleringstekst wordt nooit getoond (zie
 * "GEEN ANNULERINGSBLOK" hierboven); de taalgidsen worden vertaald naar
 * leesbare taalnamen (`languageCodeLabel`), nooit rauw doorgegeven.
 */

const CARD = "bg-white border border-gray-200 rounded-lg p-6 lg:p-8";
const BODY = "text-[15px] text-gray-700 leading-relaxed";

function durationText(duration, t, locale) {
  if (!duration) return null;
  if (duration.kind === "fixed") {
    return formatDurationMinutes(duration.minutes, locale);
  }
  const from = formatDurationMinutes(duration.fromMinutes, locale);
  const to = formatDurationMinutes(duration.toMinutes, locale);
  return from && to ? t("deal.viator.duration_range", { from, to }) : null;
}

function pickImageSrc(variants) {
  // Het kleinste formaat dat nog >= 400 breed is, anders het grootste.
  const sorted = [...variants].sort((a, b) => (a.width || 0) - (b.width || 0));
  return (sorted.find((v) => (v.width || 0) >= 400) || sorted[sorted.length - 1])
    .url;
}

/**
 * Aanbiedersfoto's van de activiteit, in onze EIGEN carrousel-stijl: één
 * grote foto met pijlen, telrand en dots, hetzelfde interactiepatroon als
 * `HotelImpressionsCarousel.js` (swipe op mobiel, klik op desktop) -- maar
 * zonder naam/sterren/stad, want dat heeft een activiteitsfoto niet. Dit
 * is de HOOFDGALERIJ van de deal NIET: die volgt de fotoregels van de
 * dealfabriek en staat bovenaan de pagina; dit is uitsluitend het
 * activiteitsdeel.
 */
function SupplierPhotoCarousel({ images, t }) {
  const [active, setActive] = useState(0);
  const touchX = useRef(null);

  if (images.length === 0) return null;
  const safeActive = active < images.length ? active : 0;
  const current = images[safeActive];
  const src = pickImageSrc(current.variants);
  const withWidth = current.variants.filter((v) => v.width);

  const go = (dir) => {
    setActive((prev) => (prev + dir + images.length) % images.length);
  };
  const onTouchStart = (e) => {
    touchX.current = e.changedTouches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <div>
      {/* GEEN kop boven de carrousel (regie-correctie 25-09-2026: "Foto's
          van de aanbieder" was een herkomstlabel, en Dennis' regel is geen
          labels in het gezicht van de reiziger). De carrousel staat
          gewoon meteen onder de activiteitstitel/-omschrijving. */}
      <div
        className="relative w-full h-[220px] sm:h-[360px] rounded-2xl overflow-hidden border border-gray-100 shadow-sm bg-gray-100"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Gewone <img>, geen next/image: Viators CDN hoeft niet via onze
            optimizer, en de varianten leveren de srcset al. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          key={src}
          src={src}
          srcSet={
            withWidth.length > 0
              ? withWidth.map((v) => `${v.url} ${v.width}w`).join(", ")
              : undefined
          }
          sizes="(min-width: 768px) 700px, 100vw"
          alt={current.caption || t("deal.viator.photo_alt")}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute top-3 right-3 bg-black/55 text-white text-xs font-medium rounded-full px-2.5 py-1">
          {safeActive + 1} / {images.length}
        </div>
        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label={t("common.prev")}
              className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white rounded-full w-9 h-9 flex items-center justify-center text-gray-800 shadow"
            >
              &#10094;
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label={t("common.next")}
              className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white rounded-full w-9 h-9 flex items-center justify-center text-gray-800 shadow"
            >
              &#10095;
            </button>
          </>
        )}
        {images.length > 1 && images.length <= 15 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActive(i)}
                aria-label={t("deal.goto_photo_tpl", { n: i + 1 })}
                className={`h-2 rounded-full transition-all ${
                  i === safeActive ? "w-5 bg-white" : "w-2 bg-white/60 hover:bg-white/80"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * In-/exclusies in de stijl van het deal-brede "Deze reis is
 * inclusief"-blok (zie packages/[category]/[dealId]/page.js, FIX 4): een
 * groen vinkje per inbegrepen punt. Niet-inbegrepen punten ernaast met een
 * grijs kruisje, zelfde kaart -- zodat het herkenbaar Favotrip blijft in
 * plaats van een los Viator-tabelletje.
 */
function InclusionsCard({ inclusions, exclusions, t }) {
  if (inclusions.length === 0 && exclusions.length === 0) return null;
  return (
    <div className={CARD}>
      {inclusions.length > 0 && (
        <div className={exclusions.length > 0 ? "mb-5" : undefined}>
          <h3 className="text-lg font-bold text-gray-900 mb-3">
            {t("deal.viator.inclusions_heading")}
          </h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {inclusions.map((item, i) => (
              <li key={i} className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 text-blue-500 flex-shrink-0"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-[15px] text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {exclusions.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-3">
            {t("deal.viator.exclusions_heading")}
          </h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {exclusions.map((item, i) => (
              <li key={i} className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 text-gray-400 flex-shrink-0"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
                <span className="text-[15px] text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StopMeta({ stop, t, locale }) {
  const parts = [];
  const duur = durationText(stop.duration, t, locale);
  if (duur) parts.push(duur);
  if (stop.admissionIncluded === "YES") parts.push(t("deal.viator.admission_yes"));
  if (stop.admissionIncluded === "NO") parts.push(t("deal.viator.admission_no"));
  if (stop.passByWithoutStopping) parts.push(t("deal.viator.pass_by"));
  if (parts.length === 0) return null;
  return <p className="text-[13px] text-gray-500">{parts.join(" · ")}</p>;
}

function StopList({ stops, t, locale }) {
  return (
    <ol className="space-y-3">
      {stops.map((stop, i) => (
        <li key={i}>
          <p className="font-medium text-gray-900">
            {stop.locationName || t("deal.viator.stop_label", { n: i + 1 })}
          </p>
          {stop.text && <p className={`${BODY} whitespace-pre-line`}>{stop.text}</p>}
          <StopMeta stop={stop} t={t} locale={locale} />
        </li>
      ))}
    </ol>
  );
}

const COURSE_KEYS = { MAIN: "course_main", STARTER: "course_starter", DESSERT: "course_dessert" };

function Itinerary({ itinerary, t, locale }) {
  switch (itinerary.type) {
    case "STANDARD":
      return <StopList stops={itinerary.stops} t={t} locale={locale} />;
    case "ACTIVITY":
      return (
        <div className="space-y-2">
          {itinerary.description && (
            <p className={`${BODY} whitespace-pre-line`}>{itinerary.description}</p>
          )}
          {itinerary.dishes.length > 0 && (
            <ul className={`${BODY} list-disc pl-5 space-y-1`}>
              {itinerary.dishes.map((dish, i) => {
                const courseKey = COURSE_KEYS[dish.course];
                return (
                  <li key={i}>
                    {courseKey && (
                      <span className="font-medium text-gray-900">
                        {t(`deal.viator.${courseKey}`)}:{" "}
                      </span>
                    )}
                    {dish.name}
                    {dish.description ? `: ${dish.description}` : ""}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      );
    case "MULTI_DAY_TOUR":
      return (
        <div className="space-y-4">
          {itinerary.days.map((day, i) => (
            <div key={i}>
              <p className="font-semibold text-gray-900">
                {day.dayNumber
                  ? t("deal.viator.day_label", { n: day.dayNumber })
                  : null}
                {day.dayNumber && day.title ? ": " : null}
                {day.title}
              </p>
              {day.stops.length > 0 && (
                <StopList stops={day.stops} t={t} locale={locale} />
              )}
              {day.accommodations.length > 0 && (
                <p className="text-[13px] text-gray-500">
                  {t("deal.viator.accommodation_label")}:{" "}
                  {day.accommodations.join("; ")}
                </p>
              )}
            </div>
          ))}
        </div>
      );
    case "HOP_ON_HOP_OFF":
      return (
        <div className="space-y-4">
          {itinerary.routes.map((route, i) => (
            <div key={i}>
              {route.name && <p className="font-semibold text-gray-900">{route.name}</p>}
              {route.operatingSchedule && (
                <p className={`${BODY} whitespace-pre-line`}>{route.operatingSchedule}</p>
              )}
              {durationText(route.duration, t, locale) && (
                <p className="text-[13px] text-gray-500">
                  {durationText(route.duration, t, locale)}
                </p>
              )}
              {route.stops.length > 0 && (
                <ol className={`${BODY} list-decimal pl-5 space-y-1`}>
                  {route.stops.map((stop, j) => (
                    <li key={j}>
                      {stop.locationName}
                      {stop.locationName && stop.text ? ": " : ""}
                      {stop.text}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          ))}
        </div>
      );
    case "UNSTRUCTURED":
      return (
        <div className="space-y-2">
          {itinerary.description && (
            <p className={`${BODY} whitespace-pre-line`}>{itinerary.description}</p>
          )}
          {itinerary.itinerary && (
            <p className={`${BODY} whitespace-pre-line`}>{itinerary.itinerary}</p>
          )}
        </div>
      );
    default:
      return null;
  }
}

function PracticalSection({ heading, children }) {
  return (
    <div>
      <h4 className="text-[15px] font-semibold text-gray-900 mb-1">{heading}</h4>
      {children}
    </div>
  );
}

export default function ViatorProductInfo({ productInfo, content, locale = "nl" }) {
  if (!productInfo && !content) return null;
  const { t } = getTranslations(locale);

  const inclusions = content?.inclusions ?? [];
  const exclusions = content?.exclusions ?? [];

  // ---- alles voor de uitklapkop "Praktisch over deze activiteit" --------
  const practicalSections = [];
  if (content) {
    if (content.description) {
      practicalSections.push(
        <PracticalSection key="description" heading={t("deal.viator.description_heading")}>
          <p className={`${BODY} whitespace-pre-line`}>{content.description}</p>
        </PracticalSection>,
      );
    }
    if (content.itinerary) {
      practicalSections.push(
        <PracticalSection key="itinerary" heading={t("deal.viator.itinerary_heading")}>
          <Itinerary itinerary={content.itinerary} t={t} locale={locale} />
        </PracticalSection>,
      );
    }
    const duur = durationText(content.duration, t, locale);
    if (duur) {
      practicalSections.push(
        <PracticalSection key="duration" heading={t("deal.viator.duration_heading")}>
          <p className={BODY}>{duur}</p>
        </PracticalSection>,
      );
    }
    if (content.additionalInfo.length > 0) {
      practicalSections.push(
        <PracticalSection key="additional_info" heading={t("deal.viator.additional_info_heading")}>
          <ul className={`${BODY} list-disc pl-5 space-y-1`}>
            {content.additionalInfo.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </PracticalSection>,
      );
    }
  }
  if (productInfo) {
    // Geen annuleringstekst uit `productInfo` (cancellationPolicyText/
    // -Type/-FullRefundHours): Dennis 25-09-2026, geen activiteit-
    // annuleringsblok meer, zie de doc bovenaan dit bestand.
    const { meetingPoints, ticketType, languageGuides } = productInfo;
    if (meetingPoints.length > 0) {
      practicalSections.push(
        <PracticalSection key="meeting_point" heading={t("deal.viator.meeting_point_heading")}>
          <ul className={`${BODY} space-y-1`}>
            {meetingPoints.map((text, i) => (
              <li key={i}>{text}</li>
            ))}
          </ul>
        </PracticalSection>,
      );
    }
    const ticketText = ticketType
      ? ticketType.text ||
        (ticketType.codeKey
          ? t(`deal.viator.ticket_type_code_${ticketType.codeKey}`, "")
          : null)
      : null;
    if (ticketText) {
      practicalSections.push(
        <PracticalSection key="ticket_type" heading={t("deal.viator.ticket_type_heading")}>
          <p className={BODY}>{ticketText}</p>
        </PracticalSection>,
      );
    }
    const guideGroups = [];
    for (const guide of languageGuides) {
      const typeKey = guide.type || "";
      let group = guideGroups.find((candidate) => candidate.typeKey === typeKey);
      if (!group) {
        group = { typeKey, type: guide.type, languages: [] };
        guideGroups.push(group);
      }
      const label = languageCodeLabel(guide.language, locale);
      if (label && !group.languages.includes(label)) group.languages.push(label);
    }
    const guideTypeLabel = (type) => {
      if (!type) return t("deal.viator.language_guide_type_unknown");
      const slug = type.toLowerCase().replace(/[^a-z0-9]+/g, "_");
      return t(`deal.viator.language_guide_type_${slug}`, type);
    };
    if (guideGroups.length > 0) {
      practicalSections.push(
        <PracticalSection key="languages" heading={t("deal.viator.languages_heading")}>
          <ul className={`${BODY} space-y-1`}>
            {guideGroups.map((group) => (
              <li key={group.typeKey}>
                <span className="font-medium text-gray-900">
                  {guideTypeLabel(group.type)}
                </span>
                {": "}
                {group.languages.join(", ")}
              </li>
            ))}
          </ul>
        </PracticalSection>,
      );
    }
  }

  const hasPhotos = Boolean(content && content.images.length > 0);
  if (!hasPhotos && inclusions.length === 0 && exclusions.length === 0 && practicalSections.length === 0) {
    return null;
  }

  // Bij een terugval op Engels (geen vertaling voor de sitetaal) tonen we
  // gewoon de Engelse tekst, ZONDER label (Dennis 25-09-2026: "geen
  // labels in het gezicht van de reiziger"; de gezamenlijke instructie:
  // "Waar geen vertaling is: de Engelse tekst, zonder label"). Er is dus
  // met opzet GEEN "Tekst van de aanbieder (Engels)"-notitie meer, ook
  // niet bij een automatische Viator-vertaling.

  return (
    <div className="mt-5 border-t border-gray-200 pt-5 space-y-5" data-viator-content="">
      {hasPhotos && <SupplierPhotoCarousel images={content.images} t={t} />}
      <InclusionsCard inclusions={inclusions} exclusions={exclusions} t={t} />
      {practicalSections.length > 0 && (
        <details className={CARD}>
          <summary className="text-lg font-bold text-gray-900 cursor-pointer select-none">
            {t("deal.viator.practical_heading")}
          </summary>
          <div className="mt-4 space-y-4">{practicalSections}</div>
        </details>
      )}
    </div>
  );
}
